import axios, { AxiosError } from 'axios';
import { Loader2, Send } from 'lucide-react';
import React, { useState } from 'react';
import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { toast } from 'react-toastify';
import { emailValidationSchema } from 'utils/schemaValidations';
import {
  API_REQUEST_STATUS_FAIL,
  API_REQUEST_STATUS_NOT_SENT,
  API_REQUEST_STATUS_PENDING,
  API_REQUEST_STATUS_SUCCESS,
} from 'utils/static/constants';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';

interface SendTicketToEmailSectionProps {
  idOrderItem: string;
  formIndex: number;
  eventId: string | null;
  handleDialogClose: () => void;
}

const SendTicketToEmailSection: React.FC<SendTicketToEmailSectionProps> = ({
  idOrderItem,
  formIndex,
  eventId,
  handleDialogClose,
}) => {
  const { t } = useTranslation();
  const [requestStatus, setRequestStatus] = useState<string>(
    API_REQUEST_STATUS_NOT_SENT,
  );

  const methodsSendEmail = useForm();
  const {
    handleSubmit: handleSendEmailSubmit,
    register,
    formState: { errors: sendEmailError },
    setError,
  } = methodsSendEmail;

  const sendTicketToEmail = async data => {
    const requestPayload = {
      email: data.email,
      orderItemId: idOrderItem,
      index: formIndex,
      event_id: eventId,
    };

    setRequestStatus(API_REQUEST_STATUS_PENDING);

    try {
      await axios.post(
        `${process.env.REACT_APP_API_URL}/order/send_attendee_form`,
        requestPayload,
      );

      setRequestStatus(API_REQUEST_STATUS_SUCCESS);
      handleDialogClose();
      toast.success(t('registrationFormsPage.ticketSent'), {
        position: 'bottom-right',
        autoClose: 3000,
      });
    } catch (error) {
      if (error instanceof AxiosError) {
        if (error.response?.status === 429) {
          setError('email', {
            message: 'emailRequestThrottle',
          });
        }
        setRequestStatus(API_REQUEST_STATUS_FAIL);
      }
    }
  };
  return (
    <form onSubmit={handleSendEmailSubmit(sendTicketToEmail)}>
      <Label htmlFor="email" className="label-input p3-medium">
        {t('registrationFormsPage.sendTicketToEmail')}
      </Label>
      <div className="flex items-center space-x-2 w-full">
        <div className="relative flex-1">
          <Input
            id="email"
            className={sendEmailError.email?.message ? 'error' : ''}
            type="text"
            placeholder="Email"
            {...register('email', {
              validate: {
                isValid: async value => {
                  const emailIsValid = await emailValidationSchema.isValid(
                    value,
                  );
                  if (!emailIsValid) {
                    return 'validEmailAddress';
                  }
                },
              },
            })}
          />
          {sendEmailError.email?.message ? (
            <p className="standard-input-error absolute bottom-[-20px] w-full">
              {t(`inputErrors.${sendEmailError.email?.message}`, {
                numberOfSeconds: 30,
              })}
            </p>
          ) : null}
        </div>
        <Button
          disabled={requestStatus === API_REQUEST_STATUS_PENDING}
          size="sm"
        >
          {requestStatus === API_REQUEST_STATUS_PENDING ? (
            <Loader2 className="animate-spin" />
          ) : (
            <Send size={16} />
          )}
          {t('common.send')}
        </Button>
      </div>
    </form>
  );
};

export default SendTicketToEmailSection;
